import { cn } from "@/lib/utils";
import { formatMoneyCompact, formatNumber } from "@/lib/format";
import type { ContractorPreview, ContractorWork } from "@/types/domain";
import { ActionBar, ActionButton, Eyebrow, ScreenTitle } from "../OnboardingShell";
import { departmentLabel } from "../lineHelpers";
import { closingLabel, type SaleCopy } from "./contractorLine";

/** Una licitación convocada: rubro, zona, entidad, monto y cierre. Abre su detalle. */
function WorkRow({
  tag,
  work,
  rubro,
  onOpen,
}: {
  tag: string;
  work: ContractorWork;
  rubro: string;
  onOpen: () => void;
}) {
  const closing = closingLabel(work.registrationEndsAt);
  return (
    <li className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full cursor-pointer flex-col gap-2 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
      >
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-slate-900">
            <span className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase text-blue-700">
              {tag}
            </span>
            {rubro}
            {work.department && ` · ${departmentLabel(work.department)}`}
          </span>
          <span className="mt-1 block text-[13px] font-semibold text-slate-700">{work.entity}</span>
          {work.description && (
            <span className="mt-0.5 block text-[12.5px] leading-[1.45] text-slate-500">
              {work.description}
            </span>
          )}
          {work.amountPen != null && (
            <span className="mt-0.5 block text-[13px] font-bold text-slate-900">
              {formatMoneyCompact(work.amountPen)}
            </span>
          )}
        </span>
        {closing && (
          <span className={cn("shrink-0 text-[12.5px] font-semibold", closing.className)}>
            {closing.label}
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * El primer resultado de quien le vende al Estado: cuántas licitaciones
 * convocadas calzan con lo que hace y las primeras, en el orden del dashboard. Si
 * puede, lo invita a subir documentos para saber en cuáles califica, sin obligarlo.
 */
export function ContractorResultStep({
  sale,
  rubro,
  preview,
  previewFailed,
  canQualify,
  onOpenWork,
  onQualify,
  onSkip,
  onBack,
  onChangeSearch,
  changeSearchHint,
}: {
  sale: SaleCopy;
  rubro: string;
  preview?: ContractorPreview;
  previewFailed: boolean;
  /** Bienes todavía no: sus bases no se leen. */
  canQualify: boolean;
  /** Abre el detalle de la licitación en el modal de siempre. */
  onOpenWork: (contractingId: number) => void;
  onQualify: () => void;
  onSkip: () => void;
  /** Vuelve a la última pregunta. */
  onBack: () => void;
  onChangeSearch: () => void;
  /** Qué ampliar si no hay ninguna: "amplía la zona o el tamaño". */
  changeSearchHint: string;
}) {
  if (previewFailed || !preview) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <Eyebrow tone="blue">Tu primer resultado</Eyebrow>
        <ScreenTitle>Tu línea quedó guardada.</ScreenTitle>
        <p className="mt-4 max-w-[560px] text-[15px] leading-[1.6] text-slate-600">
          No pudimos cargar las {sale.many} en este momento. Las vas a ver en tu panel,
          y te avisamos cuando se publique una de tu rubro.
        </p>
        <ActionBar onBack={onBack}>
          <ActionButton tone="blue" onClick={onSkip} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  const list = preview.list ?? [];
  if (preview.works === 0 || list.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[720px]">
        <Eyebrow tone="blue">Tu primer resultado</Eyebrow>
        <ScreenTitle>Hoy no hay {sale.many} convocadas de tu rubro.</ScreenTitle>
        <p className="mt-4 max-w-[560px] text-[15px] leading-[1.6] text-slate-600">
          Tu línea ya quedó guardada: te avisamos apenas se publique una. Si quieres
          ver más, {changeSearchHint}.
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={onChangeSearch}
            className="cursor-pointer rounded-full border border-blue-200 bg-white px-5 py-2.5 text-[14px] font-bold text-blue-700 hover:bg-blue-50"
          >
            Cambiar mi búsqueda
          </button>
        </div>
        <ActionBar onBack={onBack}>
          <ActionButton tone="blue" onClick={onSkip} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  const more = preview.works - list.length;

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Eyebrow tone="blue">Tu primer resultado · SEACE hoy</Eyebrow>
      <ScreenTitle>
        <span className="text-blue-700">
          {formatNumber(preview.works)}{" "}
          {preview.works === 1 ? `${sale.one} convocada` : `${sale.many} convocadas`}
        </span>{" "}
        de tu rubro por {formatMoneyCompact(preview.amountPen)}.
      </ScreenTitle>

      {preview.nextClosingDays != null && (
        <p className="mt-5 flex items-center gap-2 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-900">
          <span className="size-2 shrink-0 rounded-full bg-red-500" />
          <span>
            <strong className="font-bold">
              La más próxima cierra el registro{" "}
              {preview.nextClosingDays === 0 ? "hoy" : `en ${preview.nextClosingDays} días`}.
            </strong>{" "}
            {preview.closingThisWeek} {preview.closingThisWeek === 1 ? "cierra" : "cierran"} esta
            semana.
          </span>
        </p>
      )}

      <ul className="mt-4 overflow-hidden rounded-[18px] border border-slate-200 bg-white">
        {list.map((work) => (
          <WorkRow
            key={work.contractingId}
            tag={sale.tag}
            work={work}
            rubro={rubro}
            onOpen={() => onOpenWork(work.contractingId)}
          />
        ))}
        <li className="bg-slate-50 px-4 py-2.5 text-[12.5px] text-slate-500">
          Te las abrimos gratis: ya quedan en tu panel.
          {more > 0 &&
            ` Y ${formatNumber(more)} ${more === 1 ? sale.one : sale.many} más en tu panel.`}
        </li>
      </ul>

      <ActionBar onBack={onBack}>
        {canQualify ? (
          <ActionButton tone="blue" onClick={onQualify}>
            Ver en cuáles califico
          </ActionButton>
        ) : (
          <ActionButton tone="blue" onClick={onSkip} arrow>
            Continuar
          </ActionButton>
        )}
      </ActionBar>
    </div>
  );
}
